import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { Edit } from 'lucide-react';

import type { BookingReservationDetail } from '@/types/api/queries';

import { BookingStatusBadges } from '@/components';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { bookingDateTime, bookingTimeRange } from '@/helpers';

import {
  BOOKING_CHANNEL_LABEL_KEYS,
  BOOKING_COVERAGE_LABEL_KEYS,
  BOOKING_PAYMENT_LABEL_KEYS,
} from '@/constants';
import { DISPLAY_DATES_FORMAT } from '@/constants/dates';

import { BookingPersonName } from '../BookingPersonName';

const Row = (props: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
    <span className="text-muted-foreground">{props.label}</span>
    <span className="min-w-0">{props.children}</span>
  </div>
);

type ReservationSummaryProps = {
  reservation: BookingReservationDetail;
  isMissedCommentPending: boolean;
  onMissedCommentEdit: () => void;
};

export const ReservationSummary = (props: ReservationSummaryProps) => {
  const { reservation, isMissedCommentPending, onMissedCommentEdit } = props;
  const { t } = useTranslation();

  return (
    <div className="grid gap-2">
      <BookingStatusBadges
        status={reservation.status}
        isUnassigned={reservation.unassigned}
      />

      <Separator className="my-2" />

      <Row label={t('Service')}>{reservation.service.name}</Row>
      {!!reservation.group_session && (
        <Row label={t('Group class')}>
          {`${reservation.group_session.service.name} · ${bookingDateTime(
            reservation.group_session.starts_at,
          )}`}
        </Row>
      )}
      {!!reservation.event && (
        <Row label={t('Event')}>{reservation.event.name}</Row>
      )}
      {!!reservation.event_wave && (
        <Row label={t('Wave')}>
          {`${reservation.event_wave.name} · ${bookingTimeRange(
            reservation.event_wave.starts_at,
            reservation.event_wave.ends_at,
          )}`}
        </Row>
      )}
      {/* An event takes nobody's time, so an event registration names no specialist. */}
      {!!reservation.staff && (
        <Row label={t('Specialist')}>
          {`${reservation.staff.first_name} ${reservation.staff.last_name}`}
        </Row>
      )}
      <Row label={t('Location')}>{reservation.location.name}</Row>
      <Row label={t('Date')}>
        {format(parseISO(reservation.starts_at), DISPLAY_DATES_FORMAT)}
      </Row>
      <Row label={t('Time')}>
        {bookingTimeRange(reservation.starts_at, reservation.ends_at)}
      </Row>
      <Row label={t('Client')}>
        <BookingPersonName
          client={reservation.client}
          guestInfo={reservation.guest_info}
        />
      </Row>
      {reservation.client?.phone && (
        <Row label={t('Phone')}>{reservation.client.phone}</Row>
      )}
      <Row label={t('Channel')}>
        {t(BOOKING_CHANNEL_LABEL_KEYS[reservation.channel])}
      </Row>
      {reservation.payment_status !== 'none' && (
        <Row label={t('Payment')}>
          {t(BOOKING_PAYMENT_LABEL_KEYS[reservation.payment_status])}
        </Row>
      )}
      <Row label={t('Coverage')}>
        {t(BOOKING_COVERAGE_LABEL_KEYS[reservation.coverage_kind])}
      </Row>
      {reservation.note && <Row label={t('Note')}>{reservation.note}</Row>}
      {reservation.staff_comment && (
        <Row label={t('Trainer comment')}>{reservation.staff_comment}</Row>
      )}
      {reservation.status === 'missed' && (
        <Row label={t('No-show comment')}>
          <span className="flex items-start gap-2">
            <span className="min-w-0 wrap-break-word whitespace-pre-line">
              {reservation.missed_comment || '—'}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onMissedCommentEdit}
              disabled={isMissedCommentPending}
              aria-label={t('Edit no-show comment')}
              className="-my-2 shrink-0"
            >
              <Edit className="size-4" />
            </Button>
          </span>
        </Row>
      )}
      {reservation.cancellation_reason && (
        <Row label={t('Cancellation reason')}>
          <span className="min-w-0 wrap-break-word whitespace-pre-line">
            {reservation.cancellation_reason}
          </span>
        </Row>
      )}

      {reservation.custom_field_answers.length > 0 && (
        <>
          <Separator className="my-2" />
          {reservation.custom_field_answers.map((answer) => (
            <Row
              key={answer.id}
              label={answer.label}
            >
              {answer.value || '—'}
            </Row>
          ))}
        </>
      )}
    </div>
  );
};
